import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { switchMap, tap } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { MainTableFilterState } from 'src/app/core/state/main-table-filter.state';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ModuleNumbers } from 'src/app/core/enums/module-numbers.enum';
import { getMinPausePeriodStartAt } from '../functions/get-min-pause-period-start-at';
import {
  IDepartmentPauseLogApiGroup,
  IDepartmentPauseSettings,
} from '../models/department-pause.model';
import { DepartmentPauseApiService } from '../services/department-pause-api.service';
import {
  CancelDepartmentPause,
  CreateDepartmentPausePeriod,
  FinishDepartmentPause,
  LoadDepartmentPauseCreateAvailability,
  LoadDepartmentPauseLog,
  LoadDepartmentPauseSettings,
  UpdateDepartmentPause,
  UpdateDepartmentPauseSettings,
} from './department-pause.actions';

export interface DepartmentPauseStateModel {
  pauseLogGroups: IDepartmentPauseLogApiGroup[];
  pauseLogTotalPages: number;
  pauseLogDepartmentId: string | undefined;
  canCreatePauseFrom: string | undefined;
  createAvailabilityDepartmentId: string | undefined;
  pauseSettings: IDepartmentPauseSettings | undefined;
  pauseSettingsDepartmentId: string | undefined;
}

const DEPARTMENT_PAUSE_STATE_TOKEN = new StateToken<DepartmentPauseStateModel>('departmentPause');
const EMPTY_PAUSE_OVERVIEW_MODULES: ModuleNumbers[] = [];

@State<DepartmentPauseStateModel>({
  name: DEPARTMENT_PAUSE_STATE_TOKEN,
  defaults: {
    pauseLogGroups: [],
    pauseLogTotalPages: 0,
    pauseLogDepartmentId: undefined,
    canCreatePauseFrom: undefined,
    createAvailabilityDepartmentId: undefined,
    pauseSettings: undefined,
    pauseSettingsDepartmentId: undefined,
  },
})
@Injectable()
export class DepartmentPauseState {
  constructor(
    private service: DepartmentPauseApiService,
    private store: Store
  ) {}

  @Action(LoadDepartmentPauseCreateAvailability, { cancelUncompleted: true })
  loadCreateAvailability(
    { getState, setState }: StateContext<DepartmentPauseStateModel>,
    { departmentId }: LoadDepartmentPauseCreateAvailability
  ) {
    return this.service.getCreateAvailability(departmentId).pipe(
      tap((response) => {
        setState({
          ...getState(),
          canCreatePauseFrom: response.canCreatePauseFrom,
          createAvailabilityDepartmentId: departmentId,
        });
      })
    );
  }

  @Action(LoadDepartmentPauseSettings, { cancelUncompleted: true })
  loadPauseSettings(
    { getState, setState }: StateContext<DepartmentPauseStateModel>,
    { departmentId }: LoadDepartmentPauseSettings
  ) {
    return this.service.getPauseSettings(departmentId).pipe(
      tap((response) => {
        setState({
          ...getState(),
          pauseSettings: response,
          pauseSettingsDepartmentId: departmentId,
        });
      })
    );
  }

  @Action(UpdateDepartmentPauseSettings, { cancelUncompleted: true })
  @OnSuccess({ message: 'pauseSettingsUpdated' })
  updatePauseSettings(
    ctx: StateContext<DepartmentPauseStateModel>,
    { departmentId, settings }: UpdateDepartmentPauseSettings
  ) {
    return this.service
      .updatePauseSettings(departmentId, settings)
      .pipe(switchMap(() => ctx.dispatch(new LoadDepartmentPauseSettings(departmentId))));
  }

  @Action(LoadDepartmentPauseLog, { cancelUncompleted: true })
  loadPauseLog(
    { getState, setState }: StateContext<DepartmentPauseStateModel>,
    { departmentId, filter }: LoadDepartmentPauseLog
  ) {
    return this.service.getPauseLog(departmentId, filter).pipe(
      tap((response: ApiResponse<IDepartmentPauseLogApiGroup[]>) => {
        setState({
          ...getState(),
          pauseLogGroups: response.data ?? [],
          pauseLogTotalPages: response.totalPages,
          pauseLogDepartmentId: departmentId,
        });
      })
    );
  }

  @Action(CreateDepartmentPausePeriod, { cancelUncompleted: true })
  @OnSuccess({ message: 'pausePeriodCreated' })
  createPausePeriod(
    ctx: StateContext<DepartmentPauseStateModel>,
    { departmentId, pause }: CreateDepartmentPausePeriod
  ) {
    return this.service
      .createPausePeriod(departmentId, pause)
      .pipe(switchMap(() => ctx.dispatch(new LoadDepartmentPauseLog(departmentId))));
  }

  @Action(UpdateDepartmentPause, { cancelUncompleted: true })
  @OnSuccess({ message: 'pausePeriodUpdated' })
  updatePause(
    ctx: StateContext<DepartmentPauseStateModel>,
    { departmentId, pauseId, pause }: UpdateDepartmentPause
  ) {
    return this.service
      .updatePause(departmentId, pauseId, pause)
      .pipe(switchMap(() => this.reloadPauseLog(ctx, departmentId)));
  }

  @Action(FinishDepartmentPause, { cancelUncompleted: true })
  @OnSuccess({ message: 'finishingPauseMightTakeFewMinutes' })
  finishPause(
    ctx: StateContext<DepartmentPauseStateModel>,
    { departmentId, pauseId, finish }: FinishDepartmentPause
  ) {
    return this.service
      .finishPause(departmentId, pauseId, finish)
      .pipe(switchMap(() => this.reloadPauseLog(ctx, departmentId)));
  }

  @Action(CancelDepartmentPause, { cancelUncompleted: true })
  @OnSuccess({ message: 'pauseCanceled' })
  cancelPause(
    ctx: StateContext<DepartmentPauseStateModel>,
    { departmentId, pauseId, cancel }: CancelDepartmentPause
  ) {
    return this.service
      .cancelPause(departmentId, pauseId, cancel)
      .pipe(switchMap(() => this.reloadPauseLog(ctx, departmentId)));
  }

  private reloadPauseLog(ctx: StateContext<DepartmentPauseStateModel>, departmentId: string) {
    const currentFilter = this.store.selectSnapshot(MainTableFilterState.getCurrentFilterDetails);
    return ctx.dispatch(new LoadDepartmentPauseLog(departmentId, currentFilter));
  }

  @Selector()
  static getPauseLogGroups(state: DepartmentPauseStateModel): IDepartmentPauseLogApiGroup[] {
    return state.pauseLogGroups;
  }

  @Selector()
  static getPauseLogTotalPages(state: DepartmentPauseStateModel): number {
    return state.pauseLogTotalPages;
  }

  @Selector()
  static getCanCreatePauseFrom(state: DepartmentPauseStateModel): Date | null {
    if (!state.canCreatePauseFrom) {
      return null;
    }

    return getMinPausePeriodStartAt(state.canCreatePauseFrom);
  }

  @Selector()
  static getPauseSettings(state: DepartmentPauseStateModel): IDepartmentPauseSettings | undefined {
    return state.pauseSettings;
  }

  @Selector()
  static getPauseSettingsDepartmentId(state: DepartmentPauseStateModel): string | undefined {
    return state.pauseSettingsDepartmentId;
  }

  @Selector()
  static getVisiblePauseOverviewColumnModules(state: DepartmentPauseStateModel): ModuleNumbers[] {
    return state.pauseSettings?.visiblePauseOverviewColumnModules ?? EMPTY_PAUSE_OVERVIEW_MODULES;
  }
}
